
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import classNames from 'classnames';
import React from 'react';
import { GetServerSidePropsContext, GetStaticPropsResult } from 'next';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import styles from './index.module.scss';
import Layout from '../../components/Layout';
import { Namespace } from '../../enums/namespaces';
import { StaticPathParams } from '../../typings/staticPaths';
import { Theatre } from 'typings/api';
import AppLink from 'components/AppLink/AppLink';
import { basicSEO } from 'helpers/jsx';
import Head from 'next/head';
import { withForcePlanToLoggedInUnsubs } from 'context';
import { createRedirectIfNeeded, Permalink } from 'helpers';
import { useLang } from 'context/langContext';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import { Country } from '../../enums/country';

interface Props {
  theatres: Theatre[];
  countryCode: string;
}

const AboutUs = ({ theatres, countryCode }: Props): JSX.Element => {
  const { t } = useTranslation(Namespace.AboutUs);
  const { locale } = useLang();
  const [showMore, setShowMore] = React.useState<boolean>(false);

  return (
    <Layout countryCode={countryCode}>
      <Head>{basicSEO(t('seo:about_us_title'), t('seo:about_us_description'))}</Head>
      <main className={classNames(styles.contact, styles.about)}>
        <div className={classNames(styles.sectionWrapper)}>
          <div className={classNames(styles.heading)}>
            <h1>{t('about_project')}</h1>
          </div>
          <div>
            <Trans i18nKey={'about-us:about_project_description'} components={[<p />, <strong />]} />
          </div>
        </div>

        <div className={classNames(styles.sectionWrapper, styles.schools)}>
          <div className={classNames(styles.heading)}>
            <h2>{t('forschools_footer')}</h2>
          </div>
          <div>
            <Trans
              i18nKey={'about-us:about_schools'}
              components={[
                <AppLink href={Permalink.forSchools(locale)}>
                  <a />
                </AppLink>,
              ]}
            />
          </div>
        </div>

        <div className={classNames(styles.sectionWrapper, styles.creators)}>
          <div className={classNames(styles.heading)}>
            <h2>{countryCode !== Country.Ukraine ? t('about_creators') : t('contact:ua_contact')}</h2>
          </div>
          <div className={classNames(styles.list)}>
            <ul>
              {countryCode !== Country.Ukraine ? (
                <>
                  <li>
                    <div className={styles.img}>
                      <img src="/jpg/img-zavadil.jpg" width={270} height={320} />
                    </div>
                    <h3>{t('person_title_1')}</h3>
                    <p>{t('person_about_1')}</p>
                  </li>
                  <li>
                    <div className={styles.img}>
                      <img src="/jpg/img-hajek.jpg" width={270} height={320} />
                    </div>
                    <h3>{t('person_title_3')}</h3>
                    <p>{t('person_about_3')}</p>
                  </li>
                </>
              ) : (
                <li>
                  <div className={styles.img}>
                    <img
                      srcSet={`/jpg/img-olia.jpg 1x, /jpg/img-olia@2x.jpg 2x`}
                      src="/jpg/img-olia.jpg"
                      width={270}
                      height={320}
                    />
                  </div>

                  <h2>{t('contact:ua_contact_name')}</h2>
                </li>
              )}
            </ul>
          </div>
        </div>
        <div className={classNames(styles.sectionWrapper)}>
          <div className={classNames(styles.heading)}>
            <h2>{t('about_partners')}</h2>
          </div>
          <div className={classNames(styles.partners)}>
            <ul className={styles.icons}>
              <li className={classNames(styles.icon, styles.auraPoint)}></li>
              <li className={classNames(styles.icon, styles.ct)}></li>
              <li className={classNames(styles.icon, styles.dilia)}></li>
              <li className={classNames(styles.icon, styles.elevup)}></li>
              <li className={classNames(styles.icon, styles.intergram)}></li>
              <li className={classNames(styles.icon, styles.praha2)}></li>
              <li className={classNames(styles.icon, styles.pivoda)}></li>
              <li className={classNames(styles.icon, styles.stripe)}></li>
              <li className={classNames(styles.icon, styles.osa)}></li>
              <li className={classNames(styles.icon, styles.sramek)}></li>
            </ul>
          </div>
        </div>
        <div className={classNames(styles.sectionWrapper, styles.colaboration)}>
          <div className={classNames(styles.heading)}>
            <h2>{t('about_colaboration')}</h2>
          </div>
          <div className={classNames(styles.wrapp)}>
            <div className={classNames(styles.partners)}>
              <ul className={classNames(styles.icons, styles.theaters, !showMore ? styles.theatersLess : null)}>
                {theatres?.map((theatre, i) => {
                  if (theatre.poster_url) {
                    return (
                      <li className={classNames(styles.icon, styles.theater)} key={i}>
                        <img src={theatre.poster_url} alt={theatre.name} />
                        <p>{theatre.name}</p>
                      </li>
                    );
                  }
                })}
              </ul>

              {!showMore && (
                <div className={styles.showMore}>
                  <hr className={styles.line} />
                  <button tabIndex={-1} className="btn btn-outline-info" onClick={() => setShowMore(true)}>
                    {t('about_theaters')} ({theatres?.length - 1})
                  </button>
                </div>
              )}
            </div>
            <div className={classNames(styles.whiteBanner)}>
              <h3>{t('about_colaboration_question')}</h3>
              <a href={`mailto:${countryCode !== Country.Ukraine ? 'jeviste@dramox.cz' : 'olia.shylo@dramox.tv'}`}>
                {t('about_contact_us')}
              </a>
            </div>
          </div>
        </div>
        <div className={classNames(styles.sectionWrapper, styles.provider, styles.withBackground)}>
          <div className={classNames(styles.heading)}>
            <h2>{t('authorship_provider')}</h2>
          </div>
          <div className={classNames(styles.info)}>
            <address>
              {countryCode === Country.Ukraine ? t('contact:ua_company') : t('contact:company')}
              <br />
              {countryCode === Country.Ukraine ? t('contact:ua_address') : t('contact:address')}
              <br />
              {countryCode === Country.Ukraine ? t('contact:ua_ico') : t('contact:ico')}
            </address>
            {countryCode === Country.Czechia ? <p>{t('legislative')}</p> : null}
          </div>
        </div>
      </main>
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetStaticPropsResult<Props>> {
  const { apiClient, countryCode, locale } = await authorizeUserServerSide(ctx);

  const redirect = createRedirectIfNeeded(ctx, locale);
  if (redirect) {
    return redirect;
  }

  const theatres = await apiClient.getTheatres();

  return {
    props: { theatres, countryCode },
  };
}

export default withForcePlanToLoggedInUnsubs(AboutUs);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/about-us/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  